/* global dataLayer */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import NoSSR from 'react-no-ssr';
import Nouislider from 'nouislider';

import Currency from '@spotahome/soyuz-currency';

import FormHistogram from '../FormHistogram';

import './FormSlider.scss';
import './nouislider.scss';

const THRESHOLD_VALUE = 600;

const hasLimitChanged = (currentLimit, prevLimit) =>
  !isNaN(currentLimit) && currentLimit !== prevLimit;

const getMiddleValue = value =>
  value >= THRESHOLD_VALUE ? THRESHOLD_VALUE : value / 2;

const getHistogramRange = maxPrice => [
  { percentage: 0, value: 0 },
  { percentage: 0.5, value: getMiddleValue(maxPrice) },
  { percentage: 1, value: maxPrice }
];

const convertToNouisliderRange = range => {
  const nouisliderRange = {};
  range.forEach((item, index) => {
    if (index === 0) {
      nouisliderRange.min = item.value;
    } else if (index + 1 === range.length) {
      nouisliderRange.max = item.value;
    } else {
      const percentageString = `${(item.percentage * 100).toString()}%`;
      nouisliderRange[percentageString] = item.value;
    }
  });

  return nouisliderRange;
};

const getLabel = (value, currencyIsoCode, maxPrice, addPlusSymbol = false) => {
  const parsedValue = parseInt(value, 10);
  const appendSymbol = addPlusSymbol && parsedValue === maxPrice ? ' +' : '';

  return Currency.formatPrice(parsedValue, currencyIsoCode) + appendSymbol;
};

const FormSlider = ({
  onChange,
  currencyIsoCode,
  maxPrice,
  histogramMaxHeight = 60,
  unitPrices = [],
  sliderFrom,
  sliderTo,
  trackingPrefix
}) => {
  const sliderRef = useRef(null);
  const sliderFromRef = useRef(null);
  const sliderToRef = useRef(null);
  const sliderFromPrev = useRef(sliderFrom);
  const sliderToPrev = useRef(sliderTo);

  const updateLabels = values => {
    const [min, max] = values;

    if (sliderFromRef.current) {
      sliderFromRef.current.innerHTML = getLabel(
        min,
        currencyIsoCode,
        maxPrice
      );
    }
    if (sliderToRef.current) {
      sliderToRef.current.innerHTML = getLabel(
        max,
        currencyIsoCode,
        maxPrice,
        true
      );
    }
  };

  const setPriceSlider = (from, to) => {
    if (sliderRef.current && sliderRef.current.noUiSlider) {
      sliderRef.current.noUiSlider.set([from, to]);
    }
  };

  useEffect(() => {
    const nouisliderRange = convertToNouisliderRange(
      getHistogramRange(maxPrice)
    );
    Nouislider.create(sliderRef.current, {
      start: [sliderFrom, sliderTo],
      connect: true,
      range: nouisliderRange
    });

    sliderRef.current.noUiSlider.on('change', ([fromValue, toValue]) => {
      const fromValueParsed = parseInt(fromValue, 10);
      const toValueParsed = parseInt(toValue, 10);

      onChange({ sliderFrom: fromValueParsed, sliderTo: toValueParsed });

      // Trigger analytics tracking events
      if (typeof dataLayer !== 'undefined' && sliderFrom !== fromValueParsed) {
        dataLayer.push({ event: `${trackingPrefix}-from` });
      }
      if (typeof dataLayer !== 'undefined' && sliderTo !== toValueParsed) {
        dataLayer.push({ event: `${trackingPrefix}-to` });
      }
    });

    sliderRef.current.noUiSlider.on('update', updateLabels);

    return () => {
      if (sliderRef.current && sliderRef.current.noUiSlider) {
        sliderRef.current.noUiSlider.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (
      hasLimitChanged(sliderFrom, sliderFromPrev.current) ||
      hasLimitChanged(sliderTo, sliderToPrev.current)
    ) {
      setPriceSlider(sliderFrom, sliderTo);
    }

    sliderFromPrev.current = sliderFrom;
    sliderToPrev.current = sliderTo;
  }, [sliderFrom, sliderTo]);

  return (
    <div className="form-slider">
      <NoSSR>
        <FormHistogram
          maxHeight={histogramMaxHeight}
          histogramRange={getHistogramRange(maxPrice)}
          priceList={unitPrices}
          max={maxPrice}
          from={sliderFrom}
          to={sliderTo}
        />
      </NoSSR>
      <div ref={sliderRef} />
      <div ref={sliderFromRef} className="form-slider__from" />
      <div ref={sliderToRef} className="form-slider__to" />
    </div>
  );
};

FormSlider.propTypes = {
  onChange: PropTypes.func.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  maxPrice: PropTypes.number.isRequired,
  histogramMaxHeight: PropTypes.number,
  unitPrices: PropTypes.arrayOf(PropTypes.number),
  sliderFrom: PropTypes.number.isRequired,
  sliderTo: PropTypes.number.isRequired,
  trackingPrefix: PropTypes.string.isRequired
};

export default FormSlider;
